/**
 * Area `voice-agent-pass` of the console gateway — over `apps/api/src/routes/voice-agent-pass/` (design §33.13.1).
 * Owned by plan-021 unit **WAPP-PASS**: the agents a VoiceAgent may pass a live conversation to, saved as a derived
 * draft (ADR 0074, proposed; OD-4 "a change applies on publish"), and the VoiceAgents that pass to one (the lifecycle
 * confirmations' referrers).
 *
 * ```
 *   readVoiceAgentPassTargets      `getVoiceAgent` then `getVoiceAgentPassTargets`   GET …/pass-targets    ETag = live_version_seq
 *   changeVoiceAgentPassTargets    `setVoiceAgentPassTargets`                         PUT …/pass-targets    If-Match required
 *   listVoiceAgentPassReferrers    `listVoiceAgentPassReferrers`                      GET …/pass-referrers
 * ```
 *
 * THE ETAG IS THE VOICEAGENT'S `live_version_seq`, READ FIRST. The list's ETag is `"<live_version_seq>"`, and the generated
 * client hands back no response header, so the read takes the same column from `getVoiceAgent` — BEFORE the list. A
 * version published between the two reads leaves the sequence older than the list, and the save is then the API's
 * `409 live_version_seq_stale` (the panel re-reads and says so); it can never be newer than the list it guards.
 *
 * A REFUSAL KEEPS ITS DETAIL. The per-entry refusals carry `error.detail.index` and `pass_target_limit_exceeded` carries
 * `error.detail.limit`; the generated `ApiError` drops `detail`, so this slice raises {@link VoiceAgentPassRefusedError}
 * (an `ApiError`, the same code, status and field) with the detail read, so the panel can say which entry refused, in the
 * portal's language.
 *
 * The register is EMPTY: all three operations are in the assembled spec. The sandbox half is `../sandbox/voice-agent-pass.ts`.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ApiError, assertOk, type WeTalkApiClient } from "@wetalk/api-client";
import {
  VOICE_AGENT_AUDIENCE,
  VOICE_AGENT_PASS_MODE,
  VOICE_AGENT_PASS_TARGET_KEY,
  PASS_TARGET_WARNING,
  type PassTargetWarning,
  type VoiceAgentAudience,
  type VoiceAgentPassMode,
  type VoiceAgentPassTargetKey,
} from "@wetalk/domain";

import type { ConsoleGateway } from "../gateway.js";
import { MalformedResponseError, readArray, readBoolean, readRecord, readText, readWholeNumber } from "../decode.js";
import { useGateway } from "../provider.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

export const VOICE_AGENT_PASS_AVAILABILITY = ["available", "not_available_yet"] as const;
export type VoiceAgentPassAvailability = (typeof VOICE_AGENT_PASS_AVAILABILITY)[number];

/** A listed target's state now: `unavailable` — archived, or no longer readable. */
export const PASS_TARGET_STATE = ["live", "not_live", "paused", "unavailable"] as const;
export type PassTargetState = (typeof PASS_TARGET_STATE)[number];

/** A candidate's state: never `unavailable` (an archived VoiceAgent is no candidate). */
export const PASS_CANDIDATE_STATE = ["live", "not_live", "paused"] as const;
export type PassCandidateState = (typeof PASS_CANDIDATE_STATE)[number];

/** The kinds of conversation a warning names (`medium[]`). */
export const PASS_TARGET_WARNING_MEDIUM = ["phone", "web_chat"] as const;
export type PassTargetWarningMedium = (typeof PASS_TARGET_WARNING_MEDIUM)[number];

/** Why a candidate cannot be added (`refusal`). */
export const PASS_CANDIDATE_REFUSAL = ["pass_target_cannot_receive", "pass_target_audience_crossed"] as const;
export type PassCandidateRefusal = (typeof PASS_CANDIDATE_REFUSAL)[number];

export const PASS_REFERRER_LISTED_IN = ["live", "draft", "both"] as const;
export type PassReferrerListedIn = (typeof PASS_REFERRER_LISTED_IN)[number];

/** Something to know about an entry — never a refusal (`VoiceAgentPassTargetWarning`). */
export interface PassTargetWarningView {
  readonly code: PassTargetWarning;
  /** `pass_target_no_shared_language`: the source's languages the target lacks (all of them: none shared). */
  readonly languageCode: readonly string[];
  /** `pass_target_no_shared_medium`: the kinds of conversation the source answers that the target does not. */
  readonly medium: readonly PassTargetWarningMedium[];
  /** On the save's flat list only: the entry it is about. */
  readonly targetVoiceAgentId: string | null;
}

/** One listed target (`VoiceAgentPassTargetEntry`). */
export interface PassTargetEntryView {
  readonly targetKey: VoiceAgentPassTargetKey;
  readonly targetVoiceAgentId: string;
  readonly displayName: string;
  readonly when: string;
  readonly mode: VoiceAgentPassMode;
  readonly mayPassBack: boolean;
  /** `null` on an entry passed straight away. */
  readonly note: string | null;
  readonly state: PassTargetState;
  /** `null` while unavailable. */
  readonly audience: VoiceAgentAudience | null;
  readonly warning: readonly PassTargetWarningView[];
}

/** One VoiceAgent of the workspace that could be added (`VoiceAgentPassCandidate`). */
export interface PassCandidateView {
  readonly voiceAgentId: string;
  readonly displayName: string;
  readonly agentTemplateCode: string;
  readonly audience: VoiceAgentAudience;
  readonly state: PassCandidateState;
  readonly mayReceive: boolean;
  readonly refusal: PassCandidateRefusal | null;
}

/** The published version's list, or the newest pass-target draft's. */
export interface PassTargetListView {
  readonly agentVersionId: string;
  readonly label: string;
  readonly target: readonly PassTargetEntryView[];
}

export interface PassTargetDraftView extends PassTargetListView {
  readonly baseAgentVersionId: string;
}

/** `GET …/pass-targets`, with the ETag it was read under. */
export interface VoiceAgentPassTargetsView {
  readonly availability: VoiceAgentPassAvailability;
  /** Whether this kind of agent passes conversations at all (the catalogue). */
  readonly offered: boolean;
  /** What a change names in `If-Match` — the VoiceAgent's `live_version_seq`, read before the list. */
  readonly liveVersionSeq: number;
  readonly liveVersion: PassTargetListView | null;
  readonly draft: PassTargetDraftView | null;
  readonly candidate: readonly PassCandidateView[];
}

/** One entry of a save: the whole list is sent, in display order. */
export interface PassTargetChange {
  readonly targetVoiceAgentId: string;
  readonly when: string;
  readonly mode: VoiceAgentPassMode;
  readonly mayPassBack: boolean;
  /** `null` on a cold entry, or when nothing is to be told. */
  readonly note: string | null;
}

/** `PUT …/pass-targets`'s answer (`VoiceAgentPassTargetsSaved`). `changed: false` — nothing was saved. */
export interface VoiceAgentPassTargetsSavedView {
  readonly changed: boolean;
  readonly draftAgentVersionId: string | null;
  readonly draftLabel: string | null;
  readonly baseAgentVersionId: string;
  readonly target: readonly PassTargetEntryView[];
  readonly warning: readonly PassTargetWarningView[];
}

/** A VoiceAgent whose live version or newest pass-target draft names this one. */
export interface PassReferrerView {
  readonly voiceAgentId: string;
  readonly displayName: string;
  readonly listedIn: PassReferrerListedIn;
}

/**
 * A pass-target refusal with its detail: `index` — the entry of the sent list it is about (the per-entry refusals and a
 * per-entry `validation_failed`); `limit` — the most entries a list may hold (`pass_target_limit_exceeded`).
 */
export class VoiceAgentPassRefusedError extends ApiError {
  readonly index: number | null;
  readonly limit: number | null;
  constructor(status: number, body: unknown, requestId: string | null) {
    super(status, body, requestId);
    const detail = (body as { error?: { detail?: unknown } } | null)?.error?.detail;
    const record = typeof detail === "object" && detail !== null ? (detail as Readonly<Record<string, unknown>>) : {};
    const index = record["index"];
    const limit = record["limit"];
    this.index = typeof index === "number" && Number.isInteger(index) && index >= 0 ? index : null;
    this.limit = typeof limit === "number" && Number.isInteger(limit) && limit >= 0 ? limit : null;
  }
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** plan-021 WAPP-PASS: the agents this VoiceAgent passes to (published and draft) and the ones it could add. */
    readVoiceAgentPassTargets(voiceAgentId: string): Promise<VoiceAgentPassTargetsView>;
    /**
     * plan-021 WAPP-PASS: save the whole list as a draft, naming the `live_version_seq` read. Raises
     * {@link VoiceAgentPassRefusedError} for a refusal with a detail, the API's `409 live_version_seq_stale` when another
     * version went live first. A list equal to the published one saves nothing (`changed: false`).
     */
    changeVoiceAgentPassTargets(
      voiceAgentId: string,
      target: readonly PassTargetChange[],
      liveVersionSeq: number,
    ): Promise<VoiceAgentPassTargetsSavedView>;
    /** plan-021 WAPP-PASS: the VoiceAgents of the workspace that pass conversations to this one. */
    listVoiceAgentPassReferrers(voiceAgentId: string): Promise<readonly PassReferrerView[]>;
  }
}

/** The methods this slice puts on `ConsoleGateway`. */
export type VoiceAgentPassGatewaySlice = Pick<
  ConsoleGateway,
  "readVoiceAgentPassTargets" | "changeVoiceAgentPassTargets" | "listVoiceAgentPassReferrers"
>;

/** This area's rows of `GATEWAY_GAP`: none — all three operations are served. */
export const voiceAgentPassGap: readonly GatewayGap[] = [];

// ── the wire ─────────────────────────────────────────────────────────────────────────────────────────────

function oneOf<T extends string>(allowed: readonly T[], value: unknown, path: string): T {
  const raw = readText(value, path);
  const found = allowed.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, allowed.join(" | "), raw);
  return found;
}

function nullableText(value: unknown, path: string): string | null {
  return value === null ? null : readText(value, path);
}

/** One `VoiceAgentPassTargetWarning`. `language_code`, `medium` and `target_voice_agent_id` are optional on the wire. */
export function passTargetWarningOf(wire: unknown, path: string): PassTargetWarningView {
  const row = readRecord(wire, path);
  return {
    code: oneOf(PASS_TARGET_WARNING, row["code"], `${path}.code`),
    languageCode:
      row["language_code"] === undefined
        ? []
        : readArray(row["language_code"], `${path}.language_code`).map((one, index) => readText(one, `${path}.language_code[${String(index)}]`)),
    medium:
      row["medium"] === undefined
        ? []
        : readArray(row["medium"], `${path}.medium`).map((one, index) => oneOf(PASS_TARGET_WARNING_MEDIUM, one, `${path}.medium[${String(index)}]`)),
    targetVoiceAgentId:
      row["target_voice_agent_id"] === undefined ? null : readText(row["target_voice_agent_id"], `${path}.target_voice_agent_id`),
  };
}

/** One `VoiceAgentPassTargetEntry`. */
export function passTargetEntryOf(wire: unknown, path: string): PassTargetEntryView {
  const row = readRecord(wire, path);
  return {
    targetKey: oneOf(VOICE_AGENT_PASS_TARGET_KEY, row["target_key"], `${path}.target_key`),
    targetVoiceAgentId: readText(row["target_voice_agent_id"], `${path}.target_voice_agent_id`),
    displayName: readText(row["display_name"], `${path}.display_name`),
    when: readText(row["when"], `${path}.when`),
    mode: oneOf(VOICE_AGENT_PASS_MODE, row["mode"], `${path}.mode`),
    mayPassBack: readBoolean(row["may_pass_back"], `${path}.may_pass_back`),
    note: nullableText(row["note"], `${path}.note`),
    state: oneOf(PASS_TARGET_STATE, row["state"], `${path}.state`),
    audience: row["audience"] === null ? null : oneOf(VOICE_AGENT_AUDIENCE, row["audience"], `${path}.audience`),
    warning: readArray(row["warning"], `${path}.warning`).map((one, index) => passTargetWarningOf(one, `${path}.warning[${String(index)}]`)),
  };
}

function targetListOf(wire: unknown, path: string): readonly PassTargetEntryView[] {
  return readArray(wire, path).map((one, index) => passTargetEntryOf(one, `${path}[${String(index)}]`));
}

/** One `VoiceAgentPassTargets`, with the sequence read before it. */
export function voiceAgentPassTargetsOf(wire: unknown, path: string, liveVersionSeq: number): VoiceAgentPassTargetsView {
  const data = readRecord(wire, path);
  const liveVersionWire = data["live_version"];
  const draft = data["draft"];
  return {
    availability: oneOf(VOICE_AGENT_PASS_AVAILABILITY, data["availability"], `${path}.availability`),
    offered: readBoolean(data["offered"], `${path}.offered`),
    liveVersionSeq,
    liveVersion:
      liveVersionWire === null
        ? null
        : (() => {
            const row = readRecord(liveVersionWire, `${path}.live_version`);
            return {
              agentVersionId: readText(row["agent_version_id"], `${path}.live_version.agent_version_id`),
              label: readText(row["label"], `${path}.live_version.label`),
              target: targetListOf(row["target"], `${path}.live_version.target`),
            };
          })(),
    draft:
      draft === null
        ? null
        : (() => {
            const row = readRecord(draft, `${path}.draft`);
            return {
              agentVersionId: readText(row["agent_version_id"], `${path}.draft.agent_version_id`),
              label: readText(row["label"], `${path}.draft.label`),
              baseAgentVersionId: readText(row["base_agent_version_id"], `${path}.draft.base_agent_version_id`),
              target: targetListOf(row["target"], `${path}.draft.target`),
            };
          })(),
    candidate: readArray(data["candidate"], `${path}.candidate`).map((one, index) => {
      const at = `${path}.candidate[${String(index)}]`;
      const row = readRecord(one, at);
      return {
        voiceAgentId: readText(row["voice_agent_id"], `${at}.voice_agent_id`),
        displayName: readText(row["display_name"], `${at}.display_name`),
        agentTemplateCode: readText(row["agent_template_code"], `${at}.agent_template_code`),
        audience: oneOf(VOICE_AGENT_AUDIENCE, row["audience"], `${at}.audience`),
        state: oneOf(PASS_CANDIDATE_STATE, row["state"], `${at}.state`),
        mayReceive: readBoolean(row["may_receive"], `${at}.may_receive`),
        refusal: row["refusal"] === null ? null : oneOf(PASS_CANDIDATE_REFUSAL, row["refusal"], `${at}.refusal`),
      };
    }),
  };
}

/** One `VoiceAgentPassTargetsSaved`. */
export function voiceAgentPassTargetsSavedOf(wire: unknown, path: string): VoiceAgentPassTargetsSavedView {
  const data = readRecord(wire, path);
  return {
    changed: readBoolean(data["changed"], `${path}.changed`),
    draftAgentVersionId: nullableText(data["draft_agent_version_id"], `${path}.draft_agent_version_id`),
    draftLabel: nullableText(data["draft_label"], `${path}.draft_label`),
    baseAgentVersionId: readText(data["base_agent_version_id"], `${path}.base_agent_version_id`),
    target: targetListOf(data["target"], `${path}.target`),
    warning: readArray(data["warning"], `${path}.warning`).map((one, index) => passTargetWarningOf(one, `${path}.warning[${String(index)}]`)),
  };
}

/** One `VoiceAgentPassReferrers`. */
export function passReferrersOf(wire: unknown, path: string): readonly PassReferrerView[] {
  const data = readRecord(wire, path);
  return readArray(data["voice_agent"], `${path}.voice_agent`).map((one, index) => {
    const at = `${path}.voice_agent[${String(index)}]`;
    const row = readRecord(one, at);
    return {
      voiceAgentId: readText(row["voice_agent_id"], `${at}.voice_agent_id`),
      displayName: readText(row["display_name"], `${at}.display_name`),
      listedIn: oneOf(PASS_REFERRER_LISTED_IN, row["in"], `${at}.in`),
    };
  });
}

function dataOf(body: unknown, operationId: string): unknown {
  return readRecord(body, operationId)["data"];
}

export function voiceAgentPassApiSlice(client: WeTalkApiClient, _context: SliceContext): VoiceAgentPassGatewaySlice {
  return {
    async readVoiceAgentPassTargets(voiceAgentId) {
      // The sequence first: see the header — a race leaves it older than the list, never newer.
      const voiceAgent = readRecord(
        dataOf(assertOk(await client.getVoiceAgent({ path: { voice_agent_id: voiceAgentId } })), "getVoiceAgent"),
        "getVoiceAgent.data",
      );
      const liveVersionSeq = readWholeNumber(voiceAgent["live_version_seq"], "getVoiceAgent.data.live_version_seq");
      const answer = await client.getVoiceAgentPassTargets({ path: { voice_agent_id: voiceAgentId } });
      return voiceAgentPassTargetsOf(dataOf(assertOk(answer), "getVoiceAgentPassTargets"), "getVoiceAgentPassTargets.data", liveVersionSeq);
    },
    async changeVoiceAgentPassTargets(voiceAgentId, target, liveVersionSeq) {
      const answer = await client.setVoiceAgentPassTargets({
        path: { voice_agent_id: voiceAgentId },
        body: {
          target: target.map((one) => ({
            target_voice_agent_id: one.targetVoiceAgentId,
            when: one.when,
            mode: one.mode,
            may_pass_back: one.mayPassBack,
            note: one.note,
          })),
        },
        header: { "if-match": `"${String(liveVersionSeq)}"` },
      });
      if (answer.status === 400 || answer.status === 422) {
        throw new VoiceAgentPassRefusedError(answer.status, answer.body, answer.requestId);
      }
      return voiceAgentPassTargetsSavedOf(dataOf(assertOk(answer), "setVoiceAgentPassTargets"), "setVoiceAgentPassTargets.data");
    },
    async listVoiceAgentPassReferrers(voiceAgentId) {
      const answer = await client.listVoiceAgentPassReferrers({ path: { voice_agent_id: voiceAgentId } });
      return passReferrersOf(dataOf(assertOk(answer), "listVoiceAgentPassReferrers"), "listVoiceAgentPassReferrers.data");
    },
  };
}

// ── the reads the panel and the lifecycle confirmations use ─────────────────────────────────────────────

/** Query keys of this area, per VoiceAgent. */
export const VOICE_AGENT_PASS_QUERY_KEY = {
  all: ["voice-agent-pass"] as const,
  targets: (voiceAgentId: string) => ["voice-agent-pass", "targets", voiceAgentId] as const,
  referrers: (voiceAgentId: string) => ["voice-agent-pass", "referrers", voiceAgentId] as const,
};

/** The VoiceAgent's pass targets. A refused or failed read leaves `data` undefined — never a remembered value. */
export function useVoiceAgentPassTargets(voiceAgentId: string): UseQueryResult<VoiceAgentPassTargetsView> {
  const gateway = useGateway();
  return useQuery({
    queryKey: VOICE_AGENT_PASS_QUERY_KEY.targets(voiceAgentId),
    queryFn: () => gateway.readVoiceAgentPassTargets(voiceAgentId),
    retry: false,
  });
}

/** The VoiceAgents that pass to this one, read only while `enabled` (a confirmation is open). */
export function useVoiceAgentPassReferrers(voiceAgentId: string, enabled: boolean): UseQueryResult<readonly PassReferrerView[]> {
  const gateway = useGateway();
  return useQuery({
    queryKey: VOICE_AGENT_PASS_QUERY_KEY.referrers(voiceAgentId),
    queryFn: () => gateway.listVoiceAgentPassReferrers(voiceAgentId),
    enabled,
    retry: false,
  });
}
